import { createApp, h, ref } from 'vue'

// Toast 配置类型
export interface ToastOptions {
  message: string
  type?: 'success' | 'error' | 'warning' | 'info'
  duration?: number
  position?: 'top' | 'center' | 'bottom'
}

// Toast 状态
const toasts = ref<Array<{
  id: number
  message: string
  type: string
  position: string
}>>([])

let toastId = 0

/**
 * Show toast notification
 * @param options ToastOptions or message string
 * @param type Default 'info', used when first param is string
 */
export const showToast = (
  options: ToastOptions | string,
  type: ToastOptions['type'] = 'info'
): void => {
  // Handle string input
  const config: ToastOptions = typeof options === 'string' 
    ? { message: options, type }
    : options

  const {
    message,
    type: toastType = 'info',
    duration = 3000,
    position = 'top'
  } = config

  const id = ++toastId

  // Create toast container if not exists
  let container = document.getElementById('global-toast-container')
  if (!container) {
    container = document.createElement('div')
    container.id = 'global-toast-container'
    container.style.cssText = `
      position: fixed;
      top: 20px;
      left: 50%;
      transform: translateX(-50%);
      z-index: 9999;
      display: flex;
      flex-direction: column;
      gap: 12px;
      pointer-events: none;
    `
    document.body.appendChild(container)
  }

  // Create toast element
  const toast = document.createElement('div')
  toast.id = `toast-${id}`
  
  // Type colors
  const colors = {
    success: { bg: '#22c55e', icon: '✓' },
    error: { bg: '#ef4444', icon: '✕' },
    warning: { bg: '#f59e0b', icon: '!' },
    info: { bg: '#3b82f6', icon: 'i' }
  }
  
  const color = colors[toastType]
  
  toast.style.cssText = `
    background-color: ${color.bg};
    color: white;
    padding: 12px 20px;
    border-radius: 12px;
    font-size: 14px;
    font-weight: 500;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    gap: 10px;
    min-width: 200px;
    max-width: 400px;
    opacity: 0;
    transform: translateY(-20px);
    transition: all 0.3s ease;
    pointer-events: auto;
  `
  
  toast.innerHTML = `
    <span style="
      width: 20px;
      height: 20px;
      background: rgba(255,255,255,0.2);
      border-radius: 50%;
      display: flex;
      align-items: center;
      justify-content: center;
      font-size: 12px;
      font-weight: bold;
    ">${color.icon}</span>
    <span>${message}</span>
  `

  container.appendChild(toast)

  // Animate in
  requestAnimationFrame(() => {
    toast.style.opacity = '1'
    toast.style.transform = 'translateY(0)'
  })

  // Auto remove
  setTimeout(() => {
    toast.style.opacity = '0'
    toast.style.transform = 'translateY(-20px)'
    setTimeout(() => {
      toast.remove()
      // Remove container if empty
      if (container && container.children.length === 0) {
        container.remove()
      }
    }, 300)
  }, duration)
}

// Shortcut functions
export const toastSuccess = (message: string, duration?: number) => 
  showToast({ message, type: 'success', duration })

export const toastError = (message: string, duration?: number) => 
  showToast({ message, type: 'error', duration })

export const toastWarning = (message: string, duration?: number) => 
  showToast({ message, type: 'warning', duration })

export const toastInfo = (message: string, duration?: number) => 
  showToast({ message, type: 'info', duration })

export default showToast
